import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  Group,
  Text,
  useCombobox,
} from "@mantine/core";
import { useState } from "react";

const DROPDOWN_WIDTH = 260;

/**
 * One compact filter button. The count rides in the button so the control
 * keeps its size no matter how many values are picked; the values themselves
 * are checked off in the dropdown.
 */
export default function FilterSelect({
  value,
  data = [],
  onChange,
  label,
  noun,
  icon,
  loading,
  onOpen,
}) {
  const [search, setSearch] = useState("");
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch("");
    },
    onDropdownOpen: () => {
      onOpen?.();
      combobox.focusSearchInput();
    },
  });

  const selected = new Set(value);
  const query = search.trim().toLowerCase();
  const options = data
    .filter((item) => item.label.toLowerCase().includes(query))
    .map((item) => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        active={selected.has(item.value)}
      >
        <Group gap="sm" wrap="nowrap">
          <Checkbox
            checked={selected.has(item.value)}
            size="xs"
            readOnly
            aria-hidden
            tabIndex={-1}
            style={{ pointerEvents: "none" }}
          />
          <Text size="sm">{item.label}</Text>
        </Group>
      </Combobox.Option>
    ));

  // Toggling leaves the dropdown open so several values can be picked in one go
  const handleSubmit = (item) =>
    onChange(
      selected.has(item)
        ? value.filter((current) => current !== item)
        : [...value, item]
    );

  const isActive = value.length > 0;

  return (
    <Combobox
      store={combobox}
      width={DROPDOWN_WIDTH}
      position="bottom-start"
      onOptionSubmit={handleSubmit}
    >
      <Combobox.Target>
        <Button
          variant={isActive ? "light" : "default"}
          px="sm"
          leftSection={icon}
          rightSection={
            isActive ? (
              <Badge size="sm" circle variant="filled">
                {value.length}
              </Badge>
            ) : (
              <Combobox.Chevron />
            )
          }
          onClick={() => combobox.toggleDropdown()}
        >
          {label}
        </Button>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={`Search ${noun}`}
        />
        <Combobox.Options mah={240} style={{ overflowY: "auto" }}>
          {loading ? (
            <Combobox.Empty>Loading…</Combobox.Empty>
          ) : options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>Nothing found</Combobox.Empty>
          )}
        </Combobox.Options>
        {isActive && (
          <Combobox.Footer>
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={() => onChange([])}
            >
              Clear {noun}
            </Button>
          </Combobox.Footer>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
}
